/**
 * Benchmark Period Analysis Card for Accountant Report View
 * Displays benchmark period details and selection rationale
 */

import React from 'react';

interface BenchmarkPeriod {
  start?: string;
  end?: string;
  type?: string;
  revenue?: number;
  variable_profit?: number;
  why_selected?: string;
}

interface BenchmarkPeriodCardProps {
  benchmarkPeriod: BenchmarkPeriod;
  formatCurrency: (amount?: number) => string;
}

export default function BenchmarkPeriodCard({
  benchmarkPeriod,
  formatCurrency
}: BenchmarkPeriodCardProps) {
  if (!benchmarkPeriod) return null;

  // Hue law (K3 review 2026-08-23): benchmark tiles are STRUCTURE — highlighting
  // two of three in orange/green is decoration, not wayfinding. All three tiles
  // read identical neutral; labels hueless uppercase; figures DM Mono.
  const tileClass = 'rounded-[2px] border p-4';
  const tileStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-elevated)',
    borderColor: 'var(--color-border)',
  };
  const labelClass = 'text-[11px] uppercase tracking-wide mb-1';
  const labelStyle: React.CSSProperties = { color: 'var(--color-text-muted)' };
  const valueClass = 'text-lg font-mono font-semibold';
  const valueStyle: React.CSSProperties = { color: 'var(--color-text-secondary)' };

  return (
    <div className="rounded-[2px] border p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h2 className="text-xl font-serif mb-4" style={{ color: 'var(--color-text-primary)', fontWeight: 400 }}>
        Benchmark Period Analysis
      </h2>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={tileClass} style={tileStyle}>
            <p className={labelClass} style={labelStyle}>Period</p>
            <p className={valueClass} style={valueStyle}>
              {benchmarkPeriod.start} to {benchmarkPeriod.end}
            </p>
            <p className="text-xs mt-1 font-mono" style={{ color: 'var(--color-text-dim)' }}>{benchmarkPeriod.type}</p>
          </div>
          <div className={tileClass} style={tileStyle}>
            <p className={labelClass} style={labelStyle}>Revenue</p>
            <p className={valueClass} style={valueStyle}>
              {formatCurrency(benchmarkPeriod.revenue)}
            </p>
          </div>
          <div className={tileClass} style={tileStyle}>
            <p className={labelClass} style={labelStyle}>Variable Profit</p>
            <p className={valueClass} style={valueStyle}>
              {formatCurrency(benchmarkPeriod.variable_profit)}
            </p>
          </div>
        </div>
        {benchmarkPeriod.why_selected && (
          <div className="rounded-[2px] p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Selection Rationale:</p>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{benchmarkPeriod.why_selected}</p>
          </div>
        )}
      </div>
    </div>
  );
}
